import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/auth/export', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-lms-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export data. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('This will permanently delete your account and all associated data. This cannot be undone. Continue?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/auth/me', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('token');
      navigate('/login');
    } catch {
      alert('Failed to delete account. Please try again.');
    }
  };

  return (
    <div className="page" style={{ maxWidth: 760, margin: '0 auto' }}>
      <h1>{t('privacy.title')}</h1>
      <p style={{ color: '#6b7280', marginBottom: 32 }}>{t('privacy.lastUpdated')}</p>

      <section aria-labelledby="data-controller">
        <h2 id="data-controller">{t('privacy.dataController')}</h2>
        <p>
          AKU Learning Platform is operated by GoPayFast. For data-related enquiries contact{' '}
          <a href="mailto:privacy@gopayfast.com">privacy@gopayfast.com</a>.
        </p>
      </section>

      <section aria-labelledby="data-collected" style={{ marginTop: 28 }}>
        <h2 id="data-collected">{t('privacy.dataCollected')}</h2>
        <ul>
          <li>Account information (name, email, password hash)</li>
          <li>Learning activity (enrollments, progress, quiz attempts)</li>
          <li>Payment records (processed via Stripe — we do not store card details)</li>
          <li>Gamification data (badges, points, streaks)</li>
          <li>Usage analytics (page views, login times)</li>
        </ul>
      </section>

      <section aria-labelledby="how-we-use" style={{ marginTop: 28 }}>
        <h2 id="how-we-use">{t('privacy.howWeUse')}</h2>
        <ul>
          <li>To deliver the learning platform and track your progress</li>
          <li>To issue certificates on course completion</li>
          <li>To process payments securely via Stripe</li>
          <li>To send transactional emails (password reset, announcements)</li>
          <li>To improve platform quality through aggregate analytics</li>
        </ul>
      </section>

      <section aria-labelledby="your-rights" style={{ marginTop: 28 }}>
        <h2 id="your-rights">{t('privacy.yourRights')}</h2>
        <p>Under GDPR you have the following rights:</p>
        <ul>
          <li>{t('privacy.rightAccess')}</li>
          <li>{t('privacy.rightRectify')}</li>
          <li>{t('privacy.rightErase')}</li>
          <li>{t('privacy.rightPortability')}</li>
        </ul>
        <p>
          You can exercise your data portability and erasure rights directly below.
          For other requests contact{' '}
          <a href="mailto:privacy@gopayfast.com">privacy@gopayfast.com</a>.
        </p>

        {user && (
          <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={handleExport}>
              {t('privacy.exportData')}
            </button>
            <button
              className="btn-danger"
              onClick={handleDelete}
              style={{ background: '#dc2626', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              {t('privacy.deleteAccount')}
            </button>
          </div>
        )}
        {!user && (
          <p style={{ marginTop: 12 }}>
            <Link to="/login">Sign in</Link> to export or delete your data.
          </p>
        )}
      </section>

      <section aria-labelledby="cookies" style={{ marginTop: 28 }}>
        <h2 id="cookies">Cookies</h2>
        <p>
          We use essential cookies required for authentication and platform
          functionality. Optional analytics cookies are only set with your
          consent. You can change your preference at any time by clearing your
          browser cookies and revisiting the site.
        </p>
      </section>

      <section aria-labelledby="contact" style={{ marginTop: 28 }}>
        <h2 id="contact">{t('privacy.contact')}</h2>
        <p>
          Email: <a href="mailto:privacy@gopayfast.com">privacy@gopayfast.com</a>
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
